import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { browserLocalPersistence, getRedirectResult, onAuthStateChanged, setPersistence, signInWithPopup, signInWithRedirect, signOut, type User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { POPUP_FAILURE_CODES, USERS_COLLECTION, auth, db, describeFirebaseError, googleProvider, isEmbedded } from '../utils/firebase';
import { withTimeout } from '../utils/async';
import { hashMasterPassword, readLocalHash, readUnlockFlag, writeLocalHash, writeUnlockFlag } from '../utils/security';
const OFFLINE_NOTICE = 'Firestore is not reachable from this network. Your master password is stored securely on this device and will sync to the cloud automatically.';
interface AuthContextValue {
  user: User | null;
  initializing: boolean;
  profileLoading: boolean;
  profileError: string | null;
  hasMasterPassword: boolean;
  unlocked: boolean;
  embedded: boolean;
  signInError: string | null;
  clearSignInError: () => void;
  signInWithGoogle: () => Promise<void>;
  createMasterPassword: (password: string) => Promise<void>;
  unlockWithMasterPassword: (password: string) => Promise<boolean>;
  changeMasterPassword: (current: string, next: string) => Promise<void>;
  lock: () => void;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [hasMasterPassword, setHasMasterPassword] = useState(false);
  const [storedHash, setStoredHash] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const embedded = isEmbedded();
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(() => {
      /* falls back to the default persistence */
    });

    // Completes the redirect flow when the popup route was not available.
    getRedirectResult(auth).catch(error => setSignInError(describeFirebaseError(error)));
    return onAuthStateChanged(auth, nextUser => {
      setUser(nextUser);
      setInitializing(false);
      if (!nextUser) {
        setUnlocked(false);
        setHasMasterPassword(false);
        setStoredHash(null);
        setProfileError(null);
      }
    });
  }, []);

  // Load the account's security profile (master password digest) from Firestore.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      setProfileLoading(true);
      setProfileError(null);

      // The device copy is authoritative for the gate, so setup never blocks
      // on the network. Firestore only refreshes it when it answers in time.
      const localHash = readLocalHash(user.uid);
      const applyHash = (hash: string | null) => {
        setStoredHash(hash);
        setHasMasterPassword(Boolean(hash));
        setUnlocked(Boolean(hash) && readUnlockFlag(user.uid));
      };
      applyHash(localHash);
      try {
        const snapshot = await withTimeout(getDoc(doc(db, USERS_COLLECTION, user.uid)), 8000, 'Security profile');
        if (cancelled) return;
        const remoteHash = snapshot.exists() ? snapshot.data().masterPasswordHash as string | undefined ?? null : null;
        if (remoteHash) {
          writeLocalHash(user.uid, remoteHash);
          applyHash(remoteHash);
        } else {
          applyHash(localHash);
        }
        setProfileError(null);
      } catch {
        if (cancelled) return;
        applyHash(localHash);
        setProfileError(OFFLINE_NOTICE);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);
  const signInWithGoogle = useCallback(async () => {
    setSignInError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      const code = typeof error === 'object' && error !== null ? String((error as {
        code?: string;
      }).code ?? '') : '';
      // The popup channel is unavailable (framed page, blocked popup, COOP).
      // Fall back to a full-page redirect, which always works top-level.
      if (POPUP_FAILURE_CODES.includes(code) && !isEmbedded()) {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      setSignInError(describeFirebaseError(error));
      throw error;
    }
  }, []);
  const clearSignInError = useCallback(() => setSignInError(null), []);

  /**
   * Mirrors the digest to Firestore in the background. The promise is never
   * awaited by the UI, so a slow or blocked connection cannot stall setup.
   */
  const persistProfile = useCallback((currentUser: User, hash: string) => {
    const write = setDoc(doc(db, USERS_COLLECTION, currentUser.uid), {
      uid: currentUser.uid,
      email: currentUser.email ?? '',
      displayName: currentUser.displayName ?? '',
      photoURL: currentUser.photoURL ?? '',
      masterPasswordHash: hash,
      updatedAt: serverTimestamp()
    }, {
      merge: true
    });
    write.then(() => setProfileError(null), () => setProfileError(OFFLINE_NOTICE));
    return write;
  }, []);
  const createMasterPassword = useCallback(async (password: string) => {
    if (!user) throw new Error('You must be signed in first.');
    const hash = await hashMasterPassword(user.uid, password);

    // Unlock immediately from the device copy…
    writeLocalHash(user.uid, hash);
    setStoredHash(hash);
    setHasMasterPassword(true);
    setUnlocked(true);
    writeUnlockFlag(user.uid, true);

    // …then let the cloud copy catch up whenever Firestore answers.
    const write = persistProfile(user, hash);
    try {
      await withTimeout(write, 5000, 'Security profile');
    } catch {
      /* already stored locally — the write completes in the background */
    }
  }, [persistProfile, user]);
  const unlockWithMasterPassword = useCallback(async (password: string) => {
    if (!user || !storedHash) return false;
    const hash = await hashMasterPassword(user.uid, password);
    if (hash !== storedHash) return false;
    setUnlocked(true);
    writeUnlockFlag(user.uid, true);
    return true;
  }, [storedHash, user]);
  const changeMasterPassword = useCallback(async (current: string, next: string) => {
    if (!user) throw new Error('You must be signed in first.');
    const currentHash = await hashMasterPassword(user.uid, current);
    if (currentHash !== storedHash) throw new Error('Your current master password is incorrect.');
    const nextHash = await hashMasterPassword(user.uid, next);
    writeLocalHash(user.uid, nextHash);
    setStoredHash(nextHash);
    writeUnlockFlag(user.uid, true);
    try {
      await withTimeout(persistProfile(user, nextHash), 5000, 'Security profile');
    } catch {
      /* stored locally — the write completes in the background */
    }
  }, [persistProfile, storedHash, user]);
  const lock = useCallback(() => {
    if (user) writeUnlockFlag(user.uid, false);
    setUnlocked(false);
  }, [user]);
  const logout = useCallback(async () => {
    if (user) writeUnlockFlag(user.uid, false);
    setUnlocked(false);
    await signOut(auth);
  }, [user]);
  const value = useMemo<AuthContextValue>(() => ({
    user,
    initializing,
    profileLoading,
    profileError,
    hasMasterPassword,
    unlocked,
    embedded,
    signInError,
    clearSignInError,
    signInWithGoogle,
    createMasterPassword,
    unlockWithMasterPassword,
    changeMasterPassword,
    lock,
    logout
  }), [user, initializing, profileLoading, profileError, hasMasterPassword, unlocked, embedded, signInError, clearSignInError, signInWithGoogle, createMasterPassword, unlockWithMasterPassword, changeMasterPassword, lock, logout]);
  return <AuthContext.Provider value={value} data-id="element-477" data-mp="33lgla04xgy83">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}